import React, { useMemo, useState } from "react";
import { fmtEur2 } from "@/components/ui-kit";
import { Input } from "@/components/ui/input";
import { AlertTriangle } from "lucide-react";
import NuovaVoceDaRiga from "./NuovaVoceDaRiga";

const CONF = { alta: "bg-emerald-100 text-emerald-800", media: "bg-amber-100 text-amber-800", bassa: "bg-rose-100 text-rose-800" };

function VoceSelect({ value, voci, onChange, idx }) {
  const groups = useMemo(() => {
    const g = {};
    voci.forEach((v) => { (g[v.category || "ALTRO"] = g[v.category || "ALTRO"] || []).push(v); });
    return g;
  }, [voci]);
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} className="w-full h-8 border border-zinc-300 rounded-sm text-xs px-1 bg-white" data-testid={`imp-voce-select-${idx}`}>
      <option value="">— nessuna voce: extra con nostro prezzo —</option>
      {Object.keys(groups).sort().map((c) => (
        <optgroup key={c} label={c}>
          {groups[c].map((v) => <option key={v.id} value={v.id}>{v.name} ({v.unit} · {fmtEur2(v.prezzo_rivendita)})</option>)}
        </optgroup>
      ))}
    </select>
  );
}

function RigaRow({ r, voci, vociMap, upd, cats, onVoceCreata }) {
  const v = vociMap[r.voce_id];
  const nostro = v ? (Number(r.qty_nostra) || 0) * v.prezzo_rivendita : (r.prezzo_nostro != null ? (Number(r.qty_nostra) || 0) * Number(r.prezzo_nostro) : null);
  const unmatched = !r.voce_id;
  return (
    <tr className={`border-t border-zinc-100 align-top ${!r.includi ? "opacity-40" : unmatched ? "bg-amber-50" : ""}`} data-testid={`imp-row-${r.idx}`}>
      <td className="px-2 py-2"><input type="checkbox" checked={r.includi} onChange={(e) => upd({ includi: e.target.checked })} data-testid={`imp-include-${r.idx}`} /></td>
      <td className="px-2 py-2 text-xs">
        <div className="font-medium text-zinc-800">{r.descrizione}</div>
        <div className="text-zinc-500 font-mono">{r.qty ?? "—"} {r.unit || ""} × {r.prezzo_unit != null ? fmtEur2(r.prezzo_unit) : "—"} = {r.totale != null ? fmtEur2(r.totale) : "—"}</div>
      </td>
      <td className="px-2 py-2 w-[320px]">
        <VoceSelect value={r.voce_id} voci={voci} idx={r.idx} onChange={(id) => upd({ voce_id: id, alternative_ids: [], confidenza: id ? "manuale" : null })} />
        <div className="mt-1 flex items-center gap-1.5">
          {r.confidenza && <span className={`text-[10px] px-1.5 rounded-sm ${CONF[r.confidenza] || "bg-sky-100 text-sky-800"}`}>{r.confidenza === "manuale" ? "scelta a mano" : `abbinamento ${r.confidenza}`}</span>}
          {unmatched && <span className="text-[10px] text-amber-800 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" />nessuna voce simile</span>}
        </div>
        {unmatched && <div className="mt-1 text-[10px] text-zinc-600">Puoi <NuovaVoceDaRiga r={r} cats={cats} onCreata={(v) => { onVoceCreata(v); upd({ voce_id: v.id, alternative_ids: [], confidenza: "manuale" }); }} /> oppure lasciarla come extra e scrivere il prezzo a destra.</div>}
      </td>
      <td className="px-2 py-2 w-24">
        <Input type="number" min={0} step="0.01" value={r.qty_nostra ?? ""} onChange={(e) => upd({ qty_nostra: e.target.value === "" ? "" : Number(e.target.value) })} className="h-8 text-xs text-right" data-testid={`imp-qty-${r.idx}`} />
        <div className="text-[10px] text-zinc-500 text-right mt-0.5">{v ? v.unit : r.unit || "pz"}</div>
      </td>
      <td className="px-2 py-2 w-28 text-right">
        {unmatched ? (
          <Input type="number" min={0} step="0.01" placeholder="€/unità" value={r.prezzo_nostro ?? ""} onChange={(e) => upd({ prezzo_nostro: e.target.value === "" ? null : Number(e.target.value) })} className="h-8 text-xs text-right border-amber-400" data-testid={`imp-prezzo-${r.idx}`} />
        ) : <div className="text-xs font-mono pt-2">{fmtEur2(v?.prezzo_rivendita)}</div>}
      </td>
      <td className="px-2 py-2 w-28 text-right text-xs font-mono pt-3" data-testid={`imp-nostro-${r.idx}`}>{nostro != null ? fmtEur2(nostro) : <span className="text-amber-700">manca prezzo</span>}</td>
    </tr>
  );
}

export default function RigheReview({ righe, setRighe, voci, vociMap, onVoceCreata }) {
  const cats = [...new Set(["EXTRA", ...voci.map((v) => v.category).filter(Boolean)])].sort();
  const upd = (idx, patch) => setRighe(righe.map((r) => (r.idx === idx ? { ...r, ...patch } : r)));
  const sorted = [...righe].sort((a, b) => (a.voce_id ? 1 : 0) - (b.voce_id ? 1 : 0) || a.idx - b.idx);
  const nUn = righe.filter((r) => r.includi && !r.voce_id).length;
  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="imp-righe-review">
      <div className="px-4 py-3 border-b bg-zinc-50 flex items-center justify-between">
        <div className="font-semibold text-sm">Righe lette dal preventivo ({righe.length})</div>
        {nUn > 0 && <div className="text-xs text-amber-800" data-testid="imp-unmatched-count">{nUn} da controllare: scegli una voce oppure metti il nostro prezzo</div>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50">
            <tr><th className="w-8"></th><th className="px-2 py-2 text-left">Concorrente</th><th className="px-2 py-2 text-left">Nostra voce</th><th className="px-2 py-2 text-right">Q.tà nostra</th><th className="px-2 py-2 text-right">Nostro €/u</th><th className="px-2 py-2 text-right">Nostro importo</th></tr>
          </thead>
          <tbody>{sorted.map((r) => <RigaRow key={r.idx} r={r} voci={voci} vociMap={vociMap} cats={cats} onVoceCreata={onVoceCreata} upd={(p) => upd(r.idx, p)} />)}</tbody>
        </table>
      </div>
    </div>
  );
}
